<template>
  <el-dialog
    v-model="dialogVisible"
    title="消息通知"
    width="calc(100vw - 32px)"
    :before-close="handleClose"
    class="message-detail-dialog"
    @close="handleClose"
  >
    <div
      v-if="loading"
      class="message-detail-loading"
    >
      <el-icon class="is-loading">
        <Loading />
      </el-icon>
      <span>加载中...</span>
    </div>
    
    <div
      v-else-if="messageData"
      class="message-detail"
    >
      <div class="message-detail-title">
        <h3>{{ messageData.title }}</h3>
      </div>
      <div class="message-detail-meta">
        <div class="meta-tags">
          <el-tag
            :type="getTypeTagType(messageData.type)"
            size="small"
            class="type-tag"
          >
            {{ getTypeLabel(messageData.type) }}
          </el-tag>
          <el-tag
            :type="messageData.isRead === '0' ? 'danger' : 'info'"
            size="small"
            class="status-tag"
          >
            {{ messageData.isRead === '0' ? '未读' : '已读' }}
          </el-tag>
        </div>
        <span
          v-if="messageData.createTime"
          class="message-time"
        >
          <el-icon class="time-icon"> <Clock /> </el-icon>
          {{ formatTime(messageData.createTime) }}
        </span>
      </div>
      <div class="message-detail-content">
        <div
          class="content-text"
          v-html="formatMessageContent(messageData.message || messageData.content || '暂无内容')"
        />
      </div>
      <div
        v-if="messageData.businessType === BusinessTypeEnum.MEMBERSHIP_VOTING.code"
        class="message-vote-section"
      >
        <div class="vote-header">
          <el-icon class="vote-icon">
            <Check />
          </el-icon>
          <span class="vote-label">理事会入会投票</span>
        </div>
        <div class="vote-actions">
          <template v-if="isVoteExpired">
            <span class="vote-expired-tip">该投票已失效</span>
          </template>
          <template v-else-if="!hasVoted">
            <el-button
              :icon="Check"
              :loading="voteLoading"
              class="vote-btn vote-btn-agree"
              @click="handleVote('1')"
            >
              赞成
            </el-button>
            <el-button
              :icon="Close"
              :loading="voteLoading"
              class="vote-btn vote-btn-oppose"
              @click="handleVote('0')"
            >
              反对
            </el-button>
          </template>
          <el-tag
            v-else
            :type="voteResult === '1' ? 'success' : (voteResult === '0' ? 'danger' : 'info')"
            size="large"
            class="voted-tag"
          >
            {{ voteResult === '1' ? '您已投【赞成】票' : (voteResult === '0' ? '您已投【反对】票' : '您已完成本次投票') }}
          </el-tag>
        </div>
        <div class="vote-tip">
          {{ isVoteExpired ? '此入会申请的投票通道已失效。' : '注：投票提交后不可更改，请谨慎操作。' }}
        </div>
      </div>
    </div>

    <div
      v-else-if="dialogVisible && !loading"
      class="message-detail-empty"
    >
      暂无消息详情
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button
          v-if="showGoToLinkButton"
          type="primary"
          class="footer-btn primary-btn"
          @click="handleGoToLink"
        >
          前往查看
        </el-button>
        <el-button
          class="footer-btn cancel-btn"
          @click="handleClose"
        >
          关闭
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { Loading, Clock, Document, Briefcase, Check, Close } from '@element-plus/icons-vue'
import { getUserMessageDetail } from '@/api/message'
import { voteForMembership, getVoteDetailsById } from '@/api/memberUnit'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getMessageTypeLabel, getMessageTypeByCode } from '@/enums/messageType'
import { formatMessageContent } from '@/utils/messageUtils'
import { BusinessTypeEnum, isAuthBusinessType } from '@/enums/BusinessTypeEnum'

export default {
  name: 'MessageDetailDialog',
  components: {
    Loading,
    Clock,
    Document,
    Briefcase,
    Check,
    Close
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    readRecordId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['update:modelValue', 'close', 'message-read'],
  data() {
    return {
      BusinessTypeEnum,
      messageData: null,
      loading: false,
      voteLoading: false,
      hasVoted: false,
      voteResult: null,
      isVoteExpired: false
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    // 当前消息的接收人是否为当前登录主体
    isRecipientCurrentUser() {
      if (!this.messageData || this.messageData.recipientId == null) return false
      const mainBody = this.$auth?.getMainBody()
      const currentMainBodyId = mainBody?.mainBodyId
      if (currentMainBodyId == null) return false
      return String(this.messageData.recipientId) === String(currentMainBodyId)
    },
    // 是否显示「前往查看」：授权类消息 或 接收人等于当前主体时展示
    showGoToLinkButton() {
      if (!this.messageData) return false
      return isAuthBusinessType(this.messageData.businessType) && this.isRecipientCurrentUser
    }
  },
  watch: {
    // 只监听 readRecordId 变化，当有 readRecordId 时自动加载消息详情
    readRecordId(newVal) {
      if (newVal) {
        this.loadMessageDetail()
      } else {
        // readRecordId 为空时，清空数据
        this.messageData = null
      }
    },
    // 监听弹窗打开/关闭
    dialogVisible(newVal) {
      if (!newVal) {
        // 弹窗关闭时，清空数据
        this.messageData = null
        this.loading = false
      } else {
        // 弹窗打开时，如果有 readRecordId 且没有数据，则加载
        if (this.readRecordId && !this.messageData && !this.loading) {
          this.loadMessageDetail()
        }
      }
    }
  },
  methods: {
    /**
     * 加载消息详情
     * 根据 readRecordId 查询消息详情并展示
     */
    async loadMessageDetail() {
      // 验证 readRecordId 是否存在
      if (!this.readRecordId) {
        ElMessage.warning('消息ID不存在')
        this.messageData = null
        return
      }
      
      // 防止重复调用：如果正在加载中，直接返回
      if (this.loading) {
        return
      }
      
      // 设置加载状态
      this.loading = true
      this.messageData = null
      this.hasVoted = false
      this.voteResult = null
      this.isVoteExpired = false
      
      try {
        // 调用接口获取消息详情
        const response = await getUserMessageDetail(this.readRecordId)
        if (response && response.code === 200 && response.data) {
          // 保存消息数据
          this.messageData = response.data

          // 如果是投票消息 (businessType === '90')，查询投票详情
          if (this.messageData.businessType === BusinessTypeEnum.MEMBERSHIP_VOTING.code && this.messageData.businessId) {
            await this.fetchVoteDetails(this.messageData.businessId)
          }
        }
      } catch (error) {
        // 接口调用异常
        console.error('加载消息详情失败:', error)
        this.messageData = null
      } finally {
        // 无论成功失败，都要关闭加载状态
        this.loading = false
      }
    },
    /**
     * 获取投票详情
     * @param {string|number} voteRecordId - 投票记录id
     */
    async fetchVoteDetails(voteRecordId) {
      try {
        const res = await getVoteDetailsById(voteRecordId)
        if (res && res.code === 200) {
          if (res.data) {
            this.isVoteExpired = false
            // 根据后端返回的数据判断是否已投票
            if (res.data.voteResult === '1' || res.data.voteResult === '0') {
              this.hasVoted = true
              this.voteResult = res.data.voteResult
            }
          } else {
            // 投票数据为 null，表示投票已失效
            this.isVoteExpired = true
          }
        }
      } catch (error) {
        console.error('获取投票详情失败:', error)
      }
    },
    /**
     * 处理投票
     * @param {string} result - 1: 赞成, 0: 反对
     */
    handleVote(result) {
      const resultText = result === '1' ? '赞成' : '反对'
      ElMessageBox.confirm(
        `确认对该入会申请投【${resultText}】票吗？提交后不可更改。`,
        '确认投票',
        {
          confirmButtonText: '确认提交',
          cancelButtonText: '取消',
          type: result === '1' ? 'success' : 'warning',
        }
      ).then(async () => {
        this.voteLoading = true
        try {
          // 在发送消息时，businessId 存储的是 portal_member_unit 表的 id
          // 但后端 vote 接口目前需要的是 applicantEnterpriseId (entId)
          // 需要确保后端接口能够处理，或者前端先查询出 entId
          // 这里先尝试调用，参数名根据后端 Controller 匹配
          await voteForMembership({
            businessId: this.messageData.businessId,
            voteResult: result
          })
          ElMessage.success('投票成功')
          this.hasVoted = true
          this.voteResult = result
        } catch (error) {
          console.error('投票失败:', error)
          // 如果后端返回了已投票的错误，也可以在这里设置 hasVoted = true
          if (error.message && error.message.includes('已经投过票')) {
            this.hasVoted = true
          }
        } finally {
          this.voteLoading = false
        }
      }).catch(() => {
        // 取消投票
      })
    },
    /**
     * 关闭弹窗
     */
    handleClose() {
      // 关闭弹窗并清空数据
      this.dialogVisible = false
      this.messageData = null
      this.loading = false
      this.$emit('message-read', this.readRecordId)
      this.$emit('close')
    },
    /**
     * 跳转：授权类消息或接收人为当前主体时，跳「我的授权」页
     */
    handleGoToLink() {
      if (!this.messageData) return
      if (isAuthBusinessType(this.messageData.businessType) && this.isRecipientCurrentUser) {
        this.$router.push('/user-center/account/my-approval')
      }
      this.handleClose()
    },
    /**
     * 获取消息类型标签类型
     * @param {string} type - 消息类型 code
     */
    getTypeTagType(type) {
      // 通过 code 获取枚举对象，然后使用 key 获取标签类型
      const typeEnum = getMessageTypeByCode(type)
      if (typeEnum) {
        const typeMap = {
          system: 'primary',
          business: 'warning',
          activity: 'success'
        }
        return typeMap[typeEnum.key] || 'info'
      }
      return 'info'
    },
    /**
     * 获取消息类型标签文本
     * @param {string} type - 消息类型 code
     */
    getTypeLabel(type) {
      // 根据 code 获取枚举标签
      return getMessageTypeLabel(type)
    },
    /**
     * 获取业务类型标签
     */
    getBusinessTypeLabel(businessTypeName) {
      return businessTypeName || '未知'
    },
    /**
     * 格式化时间
     */
    formatTime(time) {
      if (!time) return ''
      // 如果是时间戳，转换为日期
      if (typeof time === 'number' || typeof time === 'string') {
        const date = new Date(time)
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          const hours = String(date.getHours()).padStart(2, '0')
          const minutes = String(date.getMinutes()).padStart(2, '0')
          const seconds = String(date.getSeconds()).padStart(2, '0')
          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
        }
      }
      return time
    },
    /**
     * 格式化消息内容，支持 <span color='red'> 标签
     * 将 color 属性转换为 style 属性
     */
    formatMessageContent
  }
}
</script>

<style lang="scss" scoped>
.message-detail {
  padding: 0;
  
  .message-detail-title {
    margin-bottom: 20px;
    
    @media (max-width: 768px) {
      margin-bottom: 16px;
    }
    
    h3 {
      font-size: 20px;
      font-weight: 600;
      color: #000000;
      margin: 0;
      line-height: 1.5;
      letter-spacing: 0.3px;
      
      @media (max-width: 768px) {
        font-size: 18px;
        line-height: 1.4;
      }
    }
  }
  
  .message-detail-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f0f0f0;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 16px;
      padding-bottom: 16px;
    }
    
    .meta-tags {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      
      @media (max-width: 768px) {
        gap: 8px;
      }
      
      .type-tag,
      .status-tag,
      .business-tag {
        font-weight: 600;
        font-size: 15px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        gap: 5px;
        padding: 8px 14px;
        
        @media (max-width: 768px) {
          font-size: 13px;
          padding: 6px 12px;
        }
      }
      
      .business-tag {
        background-color: #fff7e6;
        border-color: #ffd591;
        color: #d46b08;
        
        .business-tag-icon {
          font-size: 14px;
        }
      }
    }
    
    .message-time {
      display: flex;
      align-items: center;
      font-size: 15px;
      font-weight: 600;
      color: #000000;
      
      @media (max-width: 768px) {
        font-size: 13px;
        width: 100%;
      }
      
      .time-icon {
        margin-right: 6px;
        font-size: 16px;
        
        @media (max-width: 768px) {
          font-size: 14px;
          margin-right: 4px;
        }
      }
    }
  }
  
  .message-detail-content {
    margin-bottom: 24px;
    
    @media (max-width: 768px) {
      margin-bottom: 16px;
    }
    
    .content-header {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      
      @media (max-width: 768px) {
        margin-bottom: 10px;
      }
      
      .content-icon {
        font-size: 16px;
        color: #0d63f3;
        margin-right: 6px;
        
        @media (max-width: 768px) {
          font-size: 14px;
          margin-right: 4px;
        }
      }
      
      .content-label {
        font-size: 15px;
        font-weight: 600;
        color: #000000;
        
        @media (max-width: 768px) {
          font-size: 14px;
        }
      }
    }
    
    .content-text {
      font-size: 16px;
      font-weight: 500;
      color: #000000;
      line-height: 1.8;
      padding: 20px;
      background: linear-gradient(135deg, #f8f9fa 0%, #f5f7fa 100%);
      border-radius: 8px;
      border-left: 4px solid #0d63f3;
      white-space: pre-wrap;
      word-break: break-word;
      min-height: 60px;
      letter-spacing: 0.2px;
      
      @media (max-width: 768px) {
        font-size: 14px;
        padding: 16px;
        line-height: 1.6;
        border-left-width: 3px;
      }
      
      // 支持 HTML 标签样式
      :deep(span) {
        display: inline;
      }
    }
  }
}

.message-detail-empty {
  text-align: center;
  padding: 60px 0;
  color: #999999;
  font-size: 14px;
  
  &::before {
    content: '📭';
    display: block;
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }
}

.message-detail-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #666666;
  font-size: 14px;
  
  .el-icon {
    font-size: 32px;
    margin-bottom: 16px;
    color: #0d63f3;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 10px;
  }
  
  .footer-btn {
    min-width: 100px;
    height: 40px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s;
    box-sizing: border-box;
    
    @media (max-width: 768px) {
      width: 100%;
      min-width: 0;
      height: 44px;
      margin-left: 0 !important;
      font-size: 15px;
    }
    
    &.mark-read-btn {
      background: #ffffff;
      color: #0d63f3;
      border: 1px solid #0d63f3;
      
      &:hover {
        background: #f0f7ff;
        border-color: #3d7ef5;
        color: #3d7ef5;
      }
    }
    
    &.cancel-btn {
      background: #ffffff;
      color: #666666;
      border: 1px solid #e0e0e0;
      
      &:hover {
        background: #f5f7fa;
        border-color: #0d63f3;
        color: #0d63f3;
      }
    }
    
    &.primary-btn {
      background: #0d63f3;
      border: none;
      
      &:hover {
        background: #3d7ef5;
      }
    }
  }
}

.message-vote-section {
  margin-top: 24px;
  padding: 24px;
  background: #fdfdfd;
  border: 1px solid #ebeef5;
  border-radius: 12px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    margin-top: 16px;
    padding: 16px;
    border-radius: 8px;
  }

  .vote-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    @media (max-width: 768px) {
      margin-bottom: 16px;
    }

    .vote-icon {
      font-size: 18px;
      color: #67c23a;
      margin-right: 8px;
      
      @media (max-width: 768px) {
        font-size: 16px;
        margin-right: 6px;
      }
    }

    .vote-label {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
      
      @media (max-width: 768px) {
        font-size: 15px;
      }
    }
  }

  .vote-actions {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 24px;
    margin-bottom: 16px;

    @media (max-width: 768px) {
      flex-direction: row;
      gap: 12px;
      margin-bottom: 12px;
    }

    .vote-btn {
      min-width: 120px;
      height: 44px;
      font-size: 15px;
      font-weight: 600;
      border-radius: 8px;
      transition: all 0.3s;

      @media (max-width: 768px) {
        flex: 1;
        min-width: 0;
        height: 48px;
        font-size: 16px;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        
        @media (max-width: 768px) {
          transform: none;
        }
      }

      &.vote-btn-agree {
        background: #0d63f3;
        border-color: #0d63f3;
        color: #fff;

        &:hover {
          background: #3d7ef5;
          border-color: #3d7ef5;
          color: #fff;
        }
      }

      &.vote-btn-oppose {
        background: #e8f0fe;
        border-color: #0d63f3;
        color: #0d63f3;

        &:hover {
          background: #d2e3fc;
          border-color: #3d7ef5;
          color: #3d7ef5;
        }
      }
    }

    .voted-tag {
      padding: 12px 24px;
      font-size: 16px;
      height: auto;
      border-radius: 8px;
      
      @media (max-width: 768px) {
        padding: 10px 16px;
        font-size: 14px;
        width: 100%;
        text-align: center;
      }
    }
  }

  .vote-tip {
    text-align: center;
    font-size: 13px;
    color: #909399;
    
    @media (max-width: 768px) {
      font-size: 12px;
      line-height: 1.5;
    }
  }
}
</style>

<!-- el-dialog is teleported to body, so its outer shell cannot be targeted by scoped styles. -->
<style lang="scss">
.el-dialog.message-detail-dialog {
  max-width: 680px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  overflow: hidden;

  .el-dialog__header {
    padding: 24px 24px 20px;
    margin-right: 0;
    border-bottom: 1px solid #f0f0f0;
  }

  .el-dialog__title {
    font-size: 18px;
    font-weight: 600;
    color: #000;
  }

  .el-dialog__headerbtn {
    top: 20px;
    width: 24px;
    height: 24px;
  }

  .el-dialog__body {
    padding: 24px;
    overflow-y: auto;
  }

  .el-dialog__footer {
    padding: 20px 24px;
    border-top: 1px solid #f0f0f0;
  }
}

@media (max-width: 768px) {
  .el-dialog.message-detail-dialog {
    width: 100vw !important;
    max-width: 100vw;
    margin: 0 !important;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
    display: flex;
    flex-direction: column;

    .el-dialog__header {
      padding: 16px 44px 16px 16px;
      flex-shrink: 0;
    }

    .el-dialog__title {
      font-size: 16px;
    }

    .el-dialog__headerbtn {
      top: 16px;
      right: 12px;
    }

    .el-dialog__body {
      flex: 1;
      min-height: 0;
      padding: 16px;
      overflow-y: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }

    .el-dialog__footer {
      padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
      flex-shrink: 0;

      .el-button + .el-button {
        margin-left: 0;
      }
    }
  }
}

@media (max-width: 480px) {
  .el-dialog.message-detail-dialog {
    .el-dialog__header {
      padding: 14px 40px 14px 12px;
    }

    .el-dialog__body {
      padding: 12px;
    }

    .el-dialog__footer {
      padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
    }
  }
}
</style>

